import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useQuery } from "react-query";

import { INIT_FILTER_VALUES } from "../../constants";
import {
  GRAPH_MODE,
  applyFlow,
  buildFilterValues,
  getModeDefaults,
  getPlainFilterValues,
  keepParametersInGroups,
} from "./filterDefaults";

/**
 * Custom hook responsible for controlling the state related to the
 * Map filter controls
 * The hook only owns state. Applying the filters to the map is done by the
 * consumer in an effect that reacts to `filterValues`, which keeps every
 * handler pure and avoids ordering races with the map loading
 * @param {string} options.mode current map mode
 * @param {function} [options.getInitialValues] optional callback that receives
 * `(filtersData, defaults)` and returns the plain values used to initialize
 * the filters (used to hydrate state from the URL)
 */
const useFilters = ({ mode, getInitialValues }) => {
  const [filterValues, setFilterValues] = useState(INIT_FILTER_VALUES);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);

  /**
   * Hit the API and get a list of options for each filter
   * The endpoint returns an object where each key/value pair
   * represents the options for a specific filter
   * This approach allows us to make a single API request instead
   * of multiple
   */
  const { data: filtersData, isError: isFiltersError } = useQuery(
    ["filtersData"],
    async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/filters`
        );
        return response;
      } catch (err) {
        console.error(err);
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  /**
   * Set the options and initial value for each filter based on
   * what is returned from the DB
   * Runs once, the first time the filter options are available
   */
  useEffect(() => {
    if (!filtersData?.data || isFiltersInitialized) return;

    const defaults = getModeDefaults(mode, filtersData.data);
    const initialValues = getInitialValues
      ? getInitialValues(filtersData.data, defaults)
      : defaults;

    setFilterValues(buildFilterValues(filtersData.data, initialValues));
    setIsFiltersInitialized(true);
  }, [filtersData?.data]); //eslint-disable-line

  /**
   * Resets the filters that are mode specific when switching modes
   * Location types, the boolean filters and the analysis selection carry over
   * @param {string} nextMode the mode being entered
   */
  const resetFiltersForMode = useCallback(
    (nextMode) => {
      if (!filtersData?.data) return;
      const defaults = getModeDefaults(nextMode, filtersData.data);

      setFilterValues((prevState) => ({
        ...buildFilterValues(filtersData.data, {
          ...getPlainFilterValues(prevState),
          parameterGroups: defaults.parameterGroups,
          parameters: defaults.parameters,
          flow: defaults.flow,
          minRecords: defaults.minRecords,
        }),
        // the dates reflect the last submitted period of record
        startDate: prevState.startDate,
        endDate: prevState.endDate,
      }));
    },
    [filtersData?.data]
  );

  /**
   * Resets every filter (except the server side dates) to the mode defaults
   * @param {string} nextMode the mode whose defaults are applied
   */
  const resetAllFilters = useCallback(
    (nextMode) => {
      if (!filtersData?.data) return;
      setFilterValues(
        buildFilterValues(
          filtersData.data,
          getModeDefaults(nextMode, filtersData.data)
        )
      );
    },
    [filtersData?.data]
  );

  const handleSelectAll = (name) => {
    setFilterValues((prevState) => {
      if (name === "parameters") {
        // Only select parameters within the selected parameter groups
        const selectedValues = keepParametersInGroups(
          filtersData?.data?.parameters?.map(({ value }) => value) || [],
          prevState.parameterGroups.value,
          filtersData?.data?.parameters
        );
        return {
          ...prevState,
          parameters: { ...prevState.parameters, value: selectedValues },
        };
      }

      return {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: filtersData?.data?.[name]?.map(({ value }) => value) || [],
        },
      };
    });
  };

  const handleSelectNone = (name) => {
    setFilterValues((prevState) => {
      const newState = {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: [],
        },
      };
      // also clears parameters when parameterGroups are cleared
      if (name === "parameterGroups" && mode === GRAPH_MODE) {
        newState.parameters = {
          ...newState.parameters,
          value: [],
        };
      }
      return newState;
    });
  };

  const handleFilterValues = (event) => {
    const { checked, name, value } = event?.target || {};
    const type = INIT_FILTER_VALUES[name]?.type;

    if (type === "multi-select" || type === "multi-select-array") {
      setFilterValues((prevState) => {
        const newValue = [...prevState[name].value];
        const existingIndex = newValue.indexOf(value);
        if (existingIndex > -1) {
          newValue.splice(existingIndex, 1);
        } else {
          newValue.push(value);
        }

        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: newValue,
          },
        };

        // also cleans parameters when a parameter group is unselected
        // only in graph mode, in explorer every parameter stays selected
        if (name === "parameterGroups" && mode === GRAPH_MODE) {
          newState.parameters = {
            ...newState.parameters,
            value: keepParametersInGroups(
              newState.parameters.value,
              newValue,
              newState.parameters.options
            ),
          };
        }

        return newState;
      });
    } else if (type === "boolean") {
      setFilterValues((prevState) => ({
        ...prevState,
        [name]: {
          ...prevState[name],
          value: checked,
        },
      }));
    } else if (name === "flow") {
      setFilterValues((prevState) => applyFlow(prevState, value));
    } else {
      setFilterValues((prevState) => ({
        ...prevState,
        [name]: {
          ...prevState[name],
          value: type === "simple-number" ? +value : value,
        },
      }));
    }
  };

  return {
    filterValues,
    handleFilterValues,
    handleSelectAll,
    handleSelectNone,
    filtersData: filtersData?.data,
    isFiltersInitialized,
    isFiltersError,
    resetFiltersForMode,
    resetAllFilters,
  };
};

export default useFilters;
